export const JSONHelper = {
  parseWithSanitize (input: string, keysToSanitize: Array<string>) {
    try {
      return JSON.parse(input)
    } catch (e) {
      for (const key of keysToSanitize) {
        input = sanitizeKey(input, key)
      }
      return JSON.parse(input)
    }
  },

  cleanJsonStringFromMD (text: string): string {
  // Regex to find a string that starts with ```json, ends with ```,
  // and captures everything in between.
  // The `s` flag allows `.` to match newlines.
    const regex = /^```json\s*([\s\S]*?)\s*```$/s

    const match = text.trim().match(regex)

    // If a match is found, return the captured group (the JSON content).
    // Otherwise, return the original string.
    if (match && match[1]) {
      return match[1]
    }

    return text
  },

  escapeNewlinesInsideJsonStrings (input: string): string {
    let out = ''
    let inString = false // currently inside "..."
    let escaped = false // previous char was \ (only meaningful inside string)

    for (let i = 0; i < input.length; i++) {
      const ch = input[i]

      if (!inString) {
        if (ch === '"') inString = true
        out += ch
        continue
      }

      // inString === true
      if (escaped) {
        out += ch
        escaped = false
        continue
      }

      if (ch === '\\') {
        out += ch
        escaped = true
        continue
      }

      if (ch === '"') {
        out += ch
        inString = false
        continue
      }

      // Replace literal line breaks inside a string with \n
      if (ch === '\n') {
        out += '\\n'
        continue
      }
      if (ch === '\r') {
        // treat CRLF as a single newline
        if (input[i + 1] === '\n') i++
        out += '\\n'
        continue
      }

      // Optional: handle Unicode line separators if they can appear
      if (ch === '\u2028') {
        out += '\\u2028'
        continue
      }
      if (ch === '\u2029') {
        out += '\\u2029'
        continue
      }

      out += ch
    }

    return out
  },

  unescapeNewlinesDeep (input: string): string {
    let out = ''
    let inString = false

    for (let i = 0; i < input.length; i++) {
      const ch = input[i]

      if (!inString) {
        if (ch === '"') inString = true
        out += ch
        continue
      }

      if (ch === '\\') {
        const next = input[i + 1]
        if (next === 'n') {
          out += '\n'
          i++
          continue
        }
        out += ch
        if (next !== undefined) {
          out += next
          i++
        }
        continue
      }

      if (ch === '"') {
        inString = false
        out += ch
        continue
      }

      out += ch
    }

    return out
  },
}

/**
 * This function will sanitize the value of a key in a JSON string.
 * Sometimes we get a JSON string like this: {"explanation": "This is a "bad" explanation"} (with nested double quotes)
 * This function will remove the nested double quotes.
 *
 * Please notice that we will pass the key for which we want to sanitize the value. This function can lead to unexpected
 * results if the key is somewhere else in the JSON string.
 *
 * @param input
 * @param key
 */
function sanitizeKey (input: string, key: string): string {
  const keyString = `"${key}":`

  const startQuoteIndex = input.indexOf(keyString) + keyString.length + 2// +2 to skip the space and the first quote

  // Check for both scenarios for the end of the explanation value
  const endQuoteIndexWithComma = input.indexOf('",\n', startQuoteIndex)
  const endQuoteIndexWithoutComma = input.indexOf('"\n', startQuoteIndex)

  let endQuoteIndex: number

  if (endQuoteIndexWithComma !== -1) {
    endQuoteIndex = endQuoteIndexWithComma
  } else if (endQuoteIndexWithoutComma !== -1) {
    endQuoteIndex = endQuoteIndexWithoutComma
  } else {
    console.error(input)
    throw new Error('Failed to locate the key value.')
  }

  const beforeKey = input.substring(0, startQuoteIndex)
  const keyContent = input.substring(startQuoteIndex, endQuoteIndex)
  const afterKey = input.substring(endQuoteIndex)

  // Remove quotes from within the key content
  const sanitizedKeyContent = keyContent.replace(/"/g, '')

  return beforeKey + sanitizedKeyContent + afterKey
}
